import React, { useState, useEffect, useRef } from 'react'
import dayjs from 'dayjs'
var relativeTime = require('dayjs/plugin/relativeTime')
dayjs.extend(relativeTime)
import styled from 'styled-components'
import { Link, useNavigate } from 'react-router-dom'
import { Formik, Field } from 'formik'
import * as UpChunk from '@mux/upchunk'
import { colors, formatSeconds } from '../ui/helpers.js'
import { H1, H3, H6, Text, Span, GradientFont, Error, SmallTitle } from '../ui/Typography.js'
import { Flex, Box, Container } from '../ui/Layout.js'
import Image from '../ui/Image.js'
import { Input } from '../ui/Input.js'
import Icon from '../ui/Icon.js'
import Button from '../ui/Button.js'
import { BigDarkSpinner } from '../ui/Spinner.js'
import SideNav, { TABS } from '../Components/SideNav.js'
import VideoPlayer from '../Components/VideoPlayer.js'
import ProcessingPreview from '../Components/ProcessingPreview.js'
import formikFormWrapper from '../formikFormWrapper.js'
import api from '../api.js'
import ChatButton from '../Components/ChatButton.js'
import { useAuth } from '../Auth.js'
import { useVideos } from '../VideoProvider.js'
import OptionsPopover from './OptionsPopover.js'
import TagsModal from './TagsModal.js'
import ModalContainer from '../ui/ModalContainer.js'
import { uniq, difference, intersection } from 'ramda'
import { getAllowedVideoDuration, getNumberOfAllowedVideos, getPlanName, hasHitVideoLimit, isFreePlan } from '../Plans.js'
import Tooltip from '../ui/Tooltip.js'
import ReactTooltip from 'react-tooltip'
import { VIDEO_TABS } from './index.js'
import Popover from '../ui/Popover.js'

export const InputContainer = styled(Flex)`
  box-shadow: 0px 1px 2px 0px rgba(50, 50, 71, 0.08), 0px 0px 1px 0px rgba(50, 50, 71, 0.2);
  height: 36px;
  border-radius: 8px;
  background-color: white;
  align-items: center;
  padding-left: 16px;
  padding-right: 16px;
  margin-right: ${(props) => props.mr || '16px'};
  width: 250px;
`

const TagContainer = styled(Flex)`
  cursor: pointer;
  width: 84px;
  min-width: 84px;
  justify-content: space-between;
  box-shadow: 0px 0px 1px 0px rgba(12, 26, 75, 0.24), 0px 3px 8px -1px rgba(50, 50, 71, 0.05);
  height: 36px;
  border-radius: 8px;
  background-color: white;
  align-items: center;
  padding-left: 16px;
  padding-right: 16px;
`

export const FilterButton = styled(Flex)`
  cursor: pointer;
  width: 92px;
  min-width: 84px;
  justify-content: space-between;
  box-shadow: 0px 0px 1px 0px rgba(12, 26, 75, 0.24), 0px 3px 8px -1px rgba(50, 50, 71, 0.05);
  height: 36px;
  border-radius: 36px;
  background-color: white;
  align-items: center;
  padding-left: 16px;
  padding-right: 16px;
`

const VerticalDivider = styled(Box)`
  border-right: 1px solid rgba(166, 183, 212, 1);
  width: 0px;
  margin-right: 16px;
  margin-left: 8px;
  height: 40px;
`

export const InputComponent = styled.input`
  ::-webkit-input-placeholder {
    font-family: 'Inter', sans-serif;
  }
  ::-moz-placeholder {
    font-family: 'Inter', sans-serif;
  }
  :-ms-input-placeholder {
    font-family: 'Inter', sans-serif;
  }
  :-moz-placeholder {
    font-family: 'Inter', sans-serif;
  }
  background: white;
  height: 36px;
  margin: 0px;
  margin-bottom: 0px;
  border-radius: 6px;
  color: white;
  display: inline-block;
  border: none;
  font-size: 14px;
  text-decoration: none;
  color: ${colors.textDark};
  transition: background 250ms ease-in-out, transform 150ms ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 100%;
`

export const FullWidthForm = styled.form`
  margin-left: 16px;
  width: 100%;
`

export const TabText = styled(Text)`
  ${(props) => !props.isActive && `color: ${colors.textLight};`}
  padding-bottom: 20px;
  ${(props) => props.isActive && 'border-bottom: 1px solid black;'}
  font-weight: 600;
  margin-right: 30px;
  cursor: pointer;
`

export const Tab = ({ onClick, title, isActive }) => {
  return (
    <TabText onClick={onClick} isActive={isActive}>
      {title}
    </TabText>
  )
}

export const ScrollButton = styled(Box)`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(270deg, #f7fafc 0%, #f7fafc 50%, rgba(255, 255, 255, 0) 100%);
  z-index: 1;
  cursor: pointer;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
  ${(props) =>
    props.direction === 'left' &&
    `
    transform: rotate(180deg);
  `}

  ${(props) => (props.direction === 'left' ? `left: -22px;` : `right: -22px;`)}
  &:hover {
    background: white;
    border-radius: 8px;
    width: 36px;
    ${(props) => (props.direction === 'left' ? `left: -10px;` : `right: -10px;`)}
  }
`

const VideoSearch = ({
  searchTerm,
  setSearchTerm,
  selectedTags,
  setPage,
  setSelectedTags,
  selectAll,
  setActiveTab,
  activeTab
}) => {
  let { user } = useAuth()
  let navigate = useNavigate()
  const { videos, setVideos, refresh } = useVideos()
  const [tempSearch, setTempSearch] = useState('')
  const [tagModalOpen, setTagModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [editTagModalOpen, setEditTagModalOpen] = useState(false)
  const [activeTag, setActiveTag] = useState()
  const [editTagText, setEditTagText] = useState('')
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const tagsContainerRef = useRef(null)
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false)

  useEffect(() => {
    if (searchTerm !== tempSearch) {
      setTempSearch(searchTerm)
    }
  }, [searchTerm])

  const submit = async (e) => {
    e?.preventDefault()
    setPage(1)
    setSearchTerm(tempSearch)
  }

  let inputAllTags = []
  videos.forEach((v) => (inputAllTags = inputAllTags.concat(v.tagsArray)))
  inputAllTags = uniq(inputAllTags.filter((el) => el))

  const deleteTag = async () => {
    const res = await api.post('/video/delete-tag', {
      tag: activeTag
    })
    refresh()
    setDeleteModalOpen(false)
    setSelectedTags([])
  }

  const editTag = async () => {
    const res = await api.post('/video/edit-tag', {
      oldTag: activeTag,
      newTag: editTagText.trim()
    })
    refresh()
    setEditTagModalOpen(false)
    setSelectedTags([])
  }

  const checkScroll = () => {
    if (tagsContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tagsContainerRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5)
    }
  }

  useEffect(() => {
    checkScroll()
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [inputAllTags])

  const scroll = (direction) => {
    if (tagsContainerRef.current) {
      const scrollAmount = 400 // Adjust this value to change scroll distance
      tagsContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  return (
    <>
      <Flex borderBottom="1px solid #EEF2F7" mb="32px" alignItems="flex-start">
        <Flex>
          <Tab onClick={() => setActiveTab(VIDEO_TABS.INDEX)} title="All videos" isActive={activeTab === VIDEO_TABS.INDEX} />
          {user.isContentCreator ? (
            <Tab
              onClick={() => setActiveTab(VIDEO_TABS.UNPUBLISHED)}
              title="Unpublished"
              isActive={activeTab === VIDEO_TABS.UNPUBLISHED}
            />
          ) : null}
        </Flex>
        <Flex alignItems="center" width="auto">
          <InputContainer>
            <Icon icon="search-grey" width={14} height={14} />
            <FullWidthForm onSubmit={submit}>
              <InputComponent placeholder="Search ..." onChange={(e) => setTempSearch(e.target.value)} value={tempSearch} />
            </FullWidthForm>
            {tempSearch || searchTerm ? (
              <Icon
                cursor="pointer"
                icon="circle-cross-grey"
                width={14}
                height={14}
                onClick={() => {
                  setSearchTerm('')
                  setTempSearch('')
                }}
              />
            ) : null}
          </InputContainer>
          <TagContainer onClick={() => setTagModalOpen(true)}>
            <Text fontWeight="400" fontSize="14px" light>
              Edit
            </Text>
            <Icon icon="label-grey" width={14} height={14} />
          </TagContainer>
        </Flex>
      </Flex>

      <Flex width="100%" maxWidth="100%">
        {inputAllTags && inputAllTags.length && inputAllTags.length > 6 ? (
          <Popover
            isOpen={filterPopoverOpen}
            onClose={() => setFilterPopoverOpen(false)}
            content={
              <Box maxHeight="300px" overflowY="auto" width="200px" p="16px" noScrollBar>
                {inputAllTags
                  .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                  .map((tag) => (
                    <Flex
                      key={tag}
                      alignItems="center"
                      p="8px"
                      cursor="pointer"
                      justifyContent="space-between"
                      onClick={() => {
                        const isSelected = selectedTags.includes(tag)
                        if (isSelected) {
                          setSelectedTags(selectedTags.filter((t) => t !== tag))
                        } else {
                          setSelectedTags([...selectedTags, tag])
                        }
                      }}
                    >
                      <Text fontWeight="400" fontSize="14px">
                        {tag}
                      </Text>
                      {selectedTags.includes(tag) && <Icon icon="tick-black" width={16} height={16} />}
                    </Flex>
                  ))}
              </Box>
            }
          >
            <FilterButton mr="24px" onClick={() => setFilterPopoverOpen(!filterPopoverOpen)}>
              <Text fontWeight="400" fontSize="14px">
                Filter
              </Text>
              <Icon icon="filter-black" width={14} height={14} />
            </FilterButton>
          </Popover>
        ) : null}
        <Box position="relative" width="calc(100% - 92px - 16px);">
          <ScrollButton direction="left" isVisible={canScrollLeft} onClick={() => scroll('left')}>
            <Icon icon="chevron-right-grey" width={24} height={24} />
          </ScrollButton>
          <Flex overflowX="scroll" noScrollBar position="relative" ref={tagsContainerRef} onScroll={checkScroll}>
            {difference(inputAllTags, selectedTags)
              .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
              .map((tag) => {
                const count = videos.filter(
                  (v) => v?.tagsArray?.includes(tag) && (!searchTerm || v?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
                )?.length
                const isSelected = selectedTags.includes(tag)
                return (
                  <Flex
                    alignItems="center"
                    bg={isSelected ? '#A6B7D4' : 'rgba(225, 232, 255, 1)'}
                    width="auto"
                    height="36px"
                    mr="8px"
                    borderRadius="20px"
                    px="16px"
                    key={tag}
                    position="relative"
                    onClick={() => {
                      if (isSelected) {
                        setSelectedTags(selectedTags.filter((t) => t !== tag))
                      } else {
                        setSelectedTags(selectedTags.concat(tag))
                      }
                      setPage(1)
                      setSearchTerm('')
                    }}
                    cursor="pointer"
                  >
                    <Text mb="2px" whiteSpace="nowrap" color={isSelected ? 'white' : colors.textLight} fontWeight="500">
                      {tag} ({count})
                    </Text>
                  </Flex>
                )
              })}
          </Flex>

          <ScrollButton direction="right" isVisible={canScrollRight} onClick={() => scroll('right')}>
            <Icon icon="chevron-right-grey" width={24} height={24} />
          </ScrollButton>
        </Box>
      </Flex>
      {selectedTags && selectedTags.length ? (
        <Flex flexWrap="wrap" mt="16px">
          {selectedTags.map((tag) => {
            return (
              <Flex
                alignItems="center"
                bg={'#A6B7D4'}
                width="auto"
                height="36px"
                mr="8px"
                borderRadius="20px"
                px="16px"
                key={tag}
                position="relative"
                onClick={() => {
                  setSelectedTags(selectedTags.filter((t) => t !== tag))
                  setPage(1)
                  setSearchTerm('')
                }}
                cursor="pointer"
              >
                <Text mb="2px" whiteSpace="nowrap" color={'white'} fontWeight="500">
                  {tag}
                </Text>
                <Icon ml="8px" width="14px" height="14px" icon="circle-cross-white" />
              </Flex>
            )
          })}
        </Flex>
      ) : null}

      <ModalContainer interiorClose p="0px" width="400px" autoHeight isOpen={tagModalOpen} setIsOpen={setTagModalOpen}>
        <Flex p="16px" borderBottom="1px solid #EDF2F7;">
          <Text fontWeight="600" fontSize="18px" width="100%">
            Manage Tags
          </Text>
        </Flex>
        <Flex p="16px" flexDirection="column" maxHeight="400px" overflowY="scroll" noScrollBar>
          {inputAllTags
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .map((tag) => {
              return (
                <Flex mb="16px" key={tag} alignItems="center" justifyContent="space-between">
                  <Flex
                    alignItems="center"
                    bg="rgba(225, 232, 255, 1)"
                    width="auto"
                    height="36px"
                    mr="8px"
                    borderRadius="20px"
                    px="16px"
                    key={tag}
                    position="relative"
                    onClick={() => {
                      setPage(1)
                      setSelectedTags([tag])
                      setSearchTerm('')
                      setTagModalOpen(false)
                    }}
                    cursor="pointer"
                  >
                    <Text mb="2px" whiteSpace="nowrap" light fontWeight="500">
                      {tag}
                    </Text>
                  </Flex>
                  <Flex width="auto" alignItems="center">
                    <Box borderRight="1px solid #EDF2F7;" pr="8px" mr="8px">
                      <Text
                        fontWeight="400"
                        cursor="pointer"
                        light
                        onClick={() => {
                          setActiveTag(tag)
                          setTagModalOpen(false)
                          setEditTagModalOpen(true)
                          setEditTagText(tag)
                        }}
                      >
                        Edit
                      </Text>
                    </Box>
                    <Text
                      fontWeight="400"
                      cursor="pointer"
                      light
                      onClick={() => {
                        setActiveTag(tag)
                        setTagModalOpen(false)
                        setDeleteModalOpen(true)
                      }}
                    >
                      Delete
                    </Text>
                  </Flex>
                </Flex>
              )
            })}
        </Flex>
      </ModalContainer>
      <ModalContainer interiorClose p="24px" width="400px" autoHeight isOpen={deleteModalOpen} setIsOpen={setDeleteModalOpen}>
        <Text fontWeight="600" fontSize="18px" width="100%" mb="8px">
          Delete Tag
        </Text>
        <Text width="100%" fontWeight="400" fontSize="14px" lightLight mb="24px">
          Deleting the "{activeTag}" tag will remove it from all content.
        </Text>
        <Flex justifyContent="flex-end">
          <Button
            zIndex={1}
            variant="grey"
            label="Cancel"
            width="80px"
            mr="16px"
            onClick={() => {
              setTagModalOpen(true)
              setDeleteModalOpen(false)
            }}
          />

          <Button zIndex={1} variant="red" label="Delete" width="80px" onClick={deleteTag} />
        </Flex>
      </ModalContainer>
      <ModalContainer interiorClose p="24px" width="400px" autoHeight isOpen={editTagModalOpen} setIsOpen={setEditTagModalOpen}>
        <Flex flexDirection="column">
          <Text fontWeight="600" fontSize="18px" width="100%" mb="8px">
            Edit Tag
          </Text>
          <Text width="100%" fontWeight="400" fontSize="14px" lightLight mb="16px">
            Edit the "{activeTag}" tag.
          </Text>
          <Input autofocus dark width="100%" mb="24px" value={editTagText} onChange={(e) => setEditTagText(e.target.value)} />
        </Flex>
        <Flex justifyContent="flex-end">
          <Button
            zIndex={1}
            variant="grey"
            label="Cancel"
            width="80px"
            mr="16px"
            onClick={() => {
              setTagModalOpen(true)
              setEditTagModalOpen(false)
            }}
          />

          <Button
            isDisabled={!editTagText || !editTagText.length}
            zIndex={1}
            variant="black"
            label="Save"
            width="80px"
            onClick={editTag}
          />
        </Flex>
      </ModalContainer>
    </>
  )
}

export default VideoSearch
